/**
 * Şans çarkının çizim geometrisi.
 *
 * İki yer aynı çarkı çizer: oyuncunun `/cark` sayfası ve yönetim panelindeki
 * **canlı önizleme** (Pazarlama & Bonus → Şans Çarkı). İkisi ayrı ayrı
 * yazılsaydı yöneticinin panelde gördüğü dizilim ile oyuncunun gördüğü çark
 * ayrışırdı — etiket taşması, renk sırası ve dilim açıları panelde doğru
 * görünüp sitede bozuk çıkardı.
 *
 * Açı sözleşmesi: **0° çarkın tepesidir** (ibrenin baktığı yön) ve açı saat
 * yönünde artar. `POST /wheel/spin` yanıtındaki dilim sırası bu sözleşmeye
 * göre yorumlanır; değiştirilirse çark kazanan dilimin üstünde durmaz.
 */

export const WHEEL_RADIUS = 84;

export const polarPoint = (angleDeg, radius) => {
  const radians = ((angleDeg - 90) * Math.PI) / 180;
  return [100 + radius * Math.cos(radians), 100 + radius * Math.sin(radians)];
};

export const segmentPath = (index, count, radius = WHEEL_RADIUS) => {
  // Tek dilimde yay başlangıcı ile bitişi aynı noktaya düşer ve SVG hiçbir
  // şey çizmez; bu durumda dilim çarkın tamamıdır.
  if (count <= 1) {
    return (
      `M 100 ${100 - radius} `
      + `A ${radius} ${radius} 0 1 1 100 ${100 + radius} `
      + `A ${radius} ${radius} 0 1 1 100 ${100 - radius} Z`
    );
  }
  const sweep = 360 / count;
  const [startX, startY] = polarPoint(index * sweep, radius);
  const [endX, endY] = polarPoint((index + 1) * sweep, radius);
  const largeArc = sweep > 180 ? 1 : 0;
  return (
    `M 100 100 L ${startX} ${startY} `
    + `A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY} Z`
  );
};

/** Dilim yazısı yarıçap boyunca (merkezden dışa) uzanır.
 *
 * Teğetsel yazıya göre iki katı yer verir — dilim sayısı arttıkça etiketin
 * kısaltılma ihtiyacı azalır. Sol yarıdaki dilimlerde yazı baş aşağı kalmasın
 * diye 180° çevrilir ve hizalama da tersine döner: çevrilmemiş yazı dış
 * kenarda *biter* (`end`), çevrilmiş yazı orada *başlar* (`start`). Hizalama
 * çevrilmezse etiket dilimin dışına taşar.
 */
export const radialLabelLayout = (center) => {
  const flipped = center > 180;
  return {
    angle: flipped ? center + 90 : center - 90,
    anchor: flipped ? 'start' : 'end',
  };
};

export const labelFontSize = (count) => {
  if (count >= 16) return 5.6;
  if (count >= 12) return 6.4;
  if (count >= 8) return 7.2;
  return 8.4;
};

/** Uzun etiket göbeğe taşar; çarkta kısaltılır, tam metin listede durur. */
export const shortLabel = (label, count) => {
  const maxLength = count >= 16 ? 13 : count >= 12 ? 15 : 18;
  const text = (label || '').trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
};

/** Dilim renginin üstünde okunan yazı rengi.
 *
 * Yazı rengi `wheel_segments.text_color` kolonundadır ve panelde uzun süre
 * sabit koyu bir değerle yazıldı: koyu bir dilim rengi seçildiğinde etiket
 * okunmuyordu ve arıza ancak çark sayfası açılınca görülüyordu. Ölçü
 * WCAG'ın göreli parlaklığıdır; eşik ortanın biraz üstündedir çünkü koyu
 * yazı açık zeminde, beyaz yazı koyu zeminde daha erken okunur hâle gelir.
 */
export const contrastTextColor = (hex) => {
  const value = String(hex || '').trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return '#0b1220';
  const channel = (start) => {
    const srgb = parseInt(value.slice(start, start + 2), 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.45 ? '#0b1220' : '#ffffff';
};
